import { PortfolioModel } from '../../portfolios/shared/models/portfolio-model';
import { Patterns } from '../../shared/validation/patterns';
import { ValidationUtils } from '../../shared/validation/validation-runner';
import { IValidator, Validator } from '../../shared/validation/validators';
import { FieldState } from '../../shared/models/field-state';
import { ApplicationMessageSource } from '../../core/models/application-message-source';
import { MessageType } from '../../core/models/enum/message-type';
import { ApplicationMessage } from '../../core/models/application-message';
import { CustomMessageService } from '../../core/message.service';
import { CustomerService } from '../shared/services/customer.service';
import { Utils } from '../../shared/utils';
import { CommonLookupItem } from '../../shared/models/common-lookup-item.model';
import {
  Component,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  EventEmitter,
} from '@angular/core';
import { CustomerModel } from '../shared/models/customer-model';
import { Locality } from '@admin/shared/models/locality.model';
import { UserService } from '@admin/core/user.service';
import { FeatureEnum } from '../../shared/models/feature-enum';
import { TranslateService } from '@ngx-translate/core';
import { AdvancedIndustry } from '@admin/shared/models/industry.model';
import { IndustryOption } from '@admin/shared/models/industry.model';
import { ConfigurationService } from '@admin/shared/services/configuration.service';

@Component({
  selector: 'hr-customer-edit-details',
  templateUrl: './customer-edit-details.component.html',
  styleUrls: ['./customer-edit-details.component.less'],
  standalone: false,
})
export class CustomerEditDetailsComponent implements OnInit, OnChanges {
  @Output()
  public onUpdate: EventEmitter<CustomerModel> =
    new EventEmitter<CustomerModel>();
  @Input('customer')
  public customerInput: CustomerModel;
  @Input()
  public localities: CommonLookupItem<number, string>[];
  @Input()
  public industries: CommonLookupItem<number, string>[];
  @Input()
  public types: CommonLookupItem<number, string>[];
  @Input()
  public customerFeedTypes: CommonLookupItem<number, string>[];
  @Input()
  public numberOfEmployees: CommonLookupItem<number, string>[];
  @Input()
  public portfolios: PortfolioModel[];
  @Input()
  public industryOptions: IndustryOption[];

  public maxIndustriesAllowed: number;

  public fieldStates: {
    customerTypeId: FieldState;
    customerFeedTypeId: FieldState;
    name: FieldState;
    name2: FieldState;
    website: FieldState;
    publicEmail: FieldState;
  };

  public hasEditCustomerPortfolioPermission: boolean;

  public selectedIndustries: AdvancedIndustry[];

  public get field() {
    return this.fieldStates;
  }

  public customer: CustomerModel;

  constructor(
    private customerService: CustomerService,
    private messageService: CustomMessageService,
    private configService: ConfigurationService,
    private translationService: TranslateService,
    private userService: UserService
  ) {
    this.customer = new CustomerModel();
    this.hasEditCustomerPortfolioPermission = false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['customerInput'] &&
      changes['customerInput'].currentValue != null
    ) {
      this.customer = Utils.cloneObject(this.customerInput, CustomerModel);
      this.initializeFieldsState();
    }
  }

  public initializeFieldsState() {
    this.fieldStates = {
      customerTypeId: new FieldState(
        () => this.customer && this.customer.customerTypeId
      ),
      customerFeedTypeId: new FieldState(
        () => this.customer && this.customer.customerFeedTypeId
      ),
      name: new FieldState(() => this.customer && this.customer.name),
      name2: new FieldState(() => this.customer && this.customer.name2),
      website: new FieldState(() => this.customer && this.customer.website),
      publicEmail: new FieldState(
        () => this.customer && this.customer.publicEmail
      ),
    };

    let portfolioSet = this.customer.customerPortfolio ? true : false;
    this.hasEditCustomerPortfolioPermission = portfolioSet
      ? this.userService.currentUser.features.some(
          (f) => f == FeatureEnum.ManagePortfolios
        )
      : this.userService.currentUser.features.some(
          (f) => f == FeatureEnum.UpdateCustomerWithoutModeration
        );
  }

  public invokeValidation(validators: IValidator[], onValid?: () => void) {
    ValidationUtils.invokeValidation(
      validators,
      this.getFieldStatesObject(),
      onValid
    );
  }

  onIndustryFilterChange(e) {
    let selected = e.value;

    if (e.value?.length > this.maxIndustriesAllowed) {
      selected.pop();
      this.customer.industries = [...selected];
    }

    if (e.itemValue.value.isHead) {
      this.onIndustryOptionRemoveClick(e.itemValue.value.industryId);
    }
  }

  checkDisabledState(industry: AdvancedIndustry[], industryId: string) {
    if (
      industry?.length === this.maxIndustriesAllowed &&
      !industry?.some((v) => v?.industryId == industryId)
    ) {
      return true;
    }
    return false;
  }

  onIndustryOptionRemoveClick(industry: string) {
    this.customer.industries = this.customer.industries.filter(
      (c: any) => c.industryId != industry
    );
  }

  onIndustryClick(event: Event, industry: AdvancedIndustry) {
    if (
      industry.isHead ||
      this.customer?.industries?.length >= this.maxIndustriesAllowed
    ) {
      event.stopPropagation();
    }
  }

  public onSaveClick() {
    let customer = this.customer;
    let varlidators = this.getValidators();

    if (customer?.industries?.length > 0) {
      this.invokeValidation(varlidators, () => {
        this.customerService.saveCustomerDetails(customer).subscribe(() => {
          this.messageService.pushMessage(<ApplicationMessage>{
            type: MessageType.Success,
            sourceId: ApplicationMessageSource.global,
            summary: '',
            detail: this.translationService.instant('message.SaveMessage'),
          });
          this.initializeFieldsState();
          this.onUpdate.emit(customer);
        });
      });
    }
  }

  public onCancelClick() {
    this.customer = Utils.cloneObject(this.customerInput, CustomerModel);
    this.initializeFieldsState();
  }

  compareLocality(option1: Locality, option2: Locality): boolean {
    return option1 && option2
      ? option1.localityId == option2.localityId
      : option1 == option2;
  }

  comparePortfolio(option1: PortfolioModel, option2: PortfolioModel): boolean {
    return option1 && option2
      ? option1.portfolioId == option2.portfolioId
      : option1 == option2;
  }

  private getFieldStatesObject(): { [index: string]: FieldState } {
    let result: { [index: string]: FieldState } = {};
    for (let p in this.fieldStates) {
      if (this.fieldStates.hasOwnProperty(p)) {
        result[this.fieldStates[p].fieldId] = this.fieldStates[p];
      }
    }

    return result;
  }

  private getValidators() {
    let field = this.field;
    let customer = this.customer;

    let emptyMessage = this.translationService.instant(
      'message.ValueLeftEmpty'
    );
    let incorrectFormat = this.translationService.instant(
      'message.IncorrectFormat'
    );
    let pattern = Patterns.websiteNew;
    let regexp = new RegExp(pattern);

    return [
      Validator.forRequired(
        field.customerTypeId,
        () => customer.customerTypeId,
        emptyMessage
      ),
      Validator.forRequired(
        field.customerFeedTypeId,
        () => customer.customerFeedTypeId,
        emptyMessage
      ),
      Validator.forRequired(field.name, () => customer.name, emptyMessage),
      Validator.forPattern(
        field.name,
        () => customer.name,
        Patterns.customerName,
        incorrectFormat
      ),
      Validator.forNotRequired(field.name2, () => customer.name2),
      Validator.forPattern(
        field.name2,
        () => customer.name2,
        Patterns.customerName,
        incorrectFormat
      ),
      Validator.forNotRequired(field.publicEmail, () => customer.publicEmail),
      Validator.forPattern(
        field.publicEmail,
        () => customer.publicEmail,
        Patterns.emailNew,
        incorrectFormat
      ),
      Validator.forNotRequired(field.website, () => customer.website),
      Validator.forPredicate(
        field.website,
        () => regexp.test(customer.website),
        incorrectFormat
      ),
    ];
  }

  ngOnInit() {
    this.maxIndustriesAllowed =
      this.configService.getCachedConfig().industryPickerMaxSelect;
  }
}
